import { useEffect, useState } from 'react';
import { GetServerSideProps } from 'next';
import Head from 'next/head.js';
import { useRouter } from 'next/router';
import Button from '@havenengineering/module-shared-library/dist/components/Button/Button';
import { Size, Theme } from '@havenengineering/module-shared-library/dist/components/Button/Button';
import { Heading } from '@havenengineering/module-shared-library/dist/components/Heading/Heading';
import InlineMessaging, {
  InlineMessagingTheme,
} from '@havenengineering/module-shared-library/dist/components/InlineMessaging/InlineMessaging';
import { LoadingIndicator } from '@havenengineering/module-shared-library/dist/components/LoadingIndicator/LoadingIndicator';

import { AlreadyLoggedInNotice } from '../components/AlreadyLoggedInNotice/AlreadyLoggedInNotice';
import { BackButton } from '../components/BackButton/BackButton';
import { Card } from '../components/Card/Card';
import DirectAccessNavigator from '../components/DirectAccessNavigator/DirectAccessNavigator';
import { FederatedLogins } from '../components/FederatedLogins/FederatedLogins';
import { FormError } from '../components/FormError/FormError';
import { LoginForm } from '../components/forms/LoginForm';
import {
  DEFAULT_ERROR_PAGE_TITLE,
  FALLBACK_ERROR_MESSAGE,
  initialErrorState,
  LOGGED_IN_ERROR_PAGE_TITLE,
} from '../helpers/errors';
import { FetchError, fetchWrapper, withIdentityApiUrl } from '../helpers/fetch';
import { errorPageView, pageView } from '../helpers/gtm';
import {
  getInteractionServerSideProps,
  getInteractionSSOServerSideProps,
  InteractionServerSideProps,
} from '../helpers/server-side-properties';
import styles from '../styles/indexPage.module.scss';

const LoginPage = ({
  clientContext,
  pageError,
  userLoggedIn,
  askUserToReAuthenticate,
}: InteractionServerSideProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(initialErrorState);

  const router = useRouter();
  const showErrorScreen = pageError || router.query?.error;
  const errorScreenTitle = userLoggedIn ? LOGGED_IN_ERROR_PAGE_TITLE : DEFAULT_ERROR_PAGE_TITLE;
  const errorScreenCode = userLoggedIn ? 400 : 500;
  const registrationPrompt = router.query && router.query['prompt'] === 'register';
  const renderSeparator =
    clientContext?.googleSignInEnabled ||
    clientContext?.appleSignInEnabled ||
    clientContext?.microsoftSignInEnabled ||
    clientContext?.registerArrivalWithoutLoginUrl ||
    clientContext?.manageBookingUrl;

  useEffect(() => {
    if (registrationPrompt) {
      const queryWithoutPromptKey = Object.keys(router.query).reduce(
        (finalQueryObject: { [key: string]: string | string[] | undefined }, queryKey: string) => {
          if (queryKey !== 'prompt') {
            finalQueryObject[queryKey] = router.query[queryKey];
          }
          return finalQueryObject;
        },
        {},
      );
      void router.push({
        pathname: '/register',
        query: queryWithoutPromptKey,
      });
      return;
    }
    // Strip prompt=login from the address bar without re-running getServerSideProps.
    // history.replaceState avoids the navigation that router.replace would trigger,
    // which would otherwise drop the dispatcher's choice of /interaction.
    if (router.query?.['prompt'] === 'login' && typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.delete('prompt');
      window.history.replaceState(null, '', url.toString());
    }
    if (showErrorScreen) {
      return errorPageView(errorScreenCode, errorScreenTitle.toLowerCase());
    }

    pageView('login', '/ga-virtual/identity/login', clientContext.id);
  }, []);

  const handleAbort = async () => {
    try {
      setLoading(true);
      const { redirectTo } = await fetchWrapper<{ redirectTo: string }>(
        withIdentityApiUrl('/interaction/abort'),
        {
          method: 'GET',
          credentials: 'include',
        },
      );
      window.location.assign(redirectTo);
    } catch (error) {
      setLoading(false);
      if (error instanceof FetchError) {
        setError({
          message: error?.message || FALLBACK_ERROR_MESSAGE,
          showCallCenter: error.showCallCenter,
          showResetPassword: error.showResetPassword,
          clientId: error.clientContext?.id || '',
        });
      } else {
        setError({
          message: FALLBACK_ERROR_MESSAGE,
          showCallCenter: false,
          showResetPassword: false,
          clientId: '',
        });
      }
    }
  };

  if (registrationPrompt) {
    return <LoadingIndicator loading={true} />;
  }

  if (showErrorScreen) {
    if (userLoggedIn) {
      // Log in re-attempt - user clicked back after login
      return <AlreadyLoggedInNotice message={errorScreenTitle} clientContext={clientContext} />;
    } else {
      // Invalid session - always show DirectAccessNavigator for ALL clients
      return <DirectAccessNavigator clientContext={clientContext} />;
    }
  }

  return (
    <>
      <Head>
        <title>{`Log in to ${clientContext.name}`}</title>
        <meta name="robots" content="noindex" />
        <meta name="googlebot" content="noindex" />
      </Head>

      <div className={styles.container}>
        <Card>
          {clientContext.displayBackOnLoginPage && (
            <BackButton text="Go back" onClick={() => handleAbort()} />
          )}
          <Heading level={2}>
            {clientContext.isThirdParty
              ? `Sign in to access ${clientContext.name}`
              : 'Log in to your account'}
          </Heading>
          {askUserToReAuthenticate && (
            <InlineMessaging
              type={InlineMessagingTheme.WARNING_THEME}
              title="Your session has expired."
              message={
                <span data-testid="login-again-message">Please log in again to continue.</span>
              }
              className={styles.mb24}
              dismissible={false}
            />
          )}
          <LoginForm clientContext={clientContext} disabled={loading} />
          {error.message && <FormError error={error} />}
          {!clientContext.isThirdParty && (
            <>
              <Button
                className={styles.registerButton}
                theme={Theme.SECONDARY_THEME}
                scale={Size.LARGE_SIZE}
                loading={loading}
                type="button"
                href={clientContext.registerUrl}
                data-testid="register-link"
              >
                Create an account
              </Button>
              {renderSeparator && (
                <div className={styles.divider}>
                  <p>or</p>
                </div>
              )}
              <FederatedLogins clientContext={clientContext} />
            </>
          )}
          {clientContext.manageBookingUrl && (
            <p className={styles.linkText}>
              <a
                data-testid="manage-booking-link"
                className={styles.secondaryLink}
                href={clientContext.manageBookingUrl}
              >
                Manage booking without an account
              </a>
            </p>
          )}

          {clientContext.registerArrivalWithoutLoginUrl && (
            <p className={styles.linkText}>
              <a
                data-testid="register-without-account-link"
                className={styles.secondaryLink}
                href={clientContext.registerArrivalWithoutLoginUrl}
              >
                Continue without account
              </a>
            </p>
          )}
        </Card>
      </div>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = (context) => {
  const hasMobileLoginCookie = !!context.req?.cookies?.['haven-mobile-login'];
  return context.query?.prompt === 'login' && !hasMobileLoginCookie
    ? getInteractionServerSideProps(context)
    : getInteractionSSOServerSideProps(context);
};

export default LoginPage;
